// books/dk.js — DraftKings V1 per-book SlipSync config.
// Single job: supply DK-specific WebView/sync parameters + the injected fetch script
// to the shared client framework.
// Spec: docs/specs/slipsync-track-b-dk.md §7 (WebSocket JSON-RPC 2.0 + Enterprise JWT,
// window.__siteExperience auto-detection, five-type postMessage protocol).
//
// DK My Bets is delivered over a WebSocket (JSON-RPC 2.0), not REST. The socket flow is
// INTERNAL to the injected script; the framework §5 postMessage contract is UNCHANGED —
// terminal messages post { detail: { type, data?, stoppedAt?, nonce } } exactly like MGM (#1409: the
// bridge's per-open nonce, from cfg).

import { isBridgeNonce } from '../constants';

// V1: NY only. Multi-state expansion requires per-state socket region/site-ID captures.
export const DK_V1_STATES = ['NY'];

const DK_WEBVIEW_URL = 'https://sportsbook.draftkings.com/mybets';
const DK_SOCKET_BASE =
  'wss://gateway.northamerica-northeast2.prod.dkapis.com/dkusny/shelby/api/v1/websocket';

// Config literal handed to the injected entrypoint. Kept exportable so the client test
// suite can invoke dkInjectedMain directly (no eval) with the exact shipped config.
export const DK_INJECT_CFG = {
  states: DK_V1_STATES,
  socketBase: DK_SOCKET_BASE,
};

// Self-contained injected entrypoint. References ONLY WebView globals (window, fetch,
// WebSocket, setTimeout, JSON) plus its cfg argument — NO module-scope closures, so
// Function.prototype.toString() serialization is faithful. Written in ES5 style so the
// serialized form runs unmodified inside WKWebView.
//
// Emits exactly ONE terminal postMessage through emit() (one-shot done guard), which
// closes the socket on every path. Five-type framework §5 protocol:
//   betslips | partial | auth_error | api_error | script_error
export function dkInjectedMain(cfg) {
  var done = false;
  var socket = null;

  function emit(msg) {
    if (done) return;
    // Load-bearing ordering: set `done` BEFORE socket.close(). Closing the socket can
    // fire onclose synchronously, which re-enters emit() — the guard above then no-ops.
    // Never move socket.close() ahead of `done = true` (reintroduces a double-emit path).
    done = true;
    try { if (socket) socket.close(); } catch (e) { /* socket may be unopened */ }
    // #1409: the bridge's per-open nonce rides on the terminal message; without it the bridge drops it.
    msg.nonce = cfg.nonce;
    window.mobileApp.postMessage({ detail: msg });
  }

  // Every script_error names its stage (2026-10-01 DK tester: a bare script_error made the
  // on-screen log unreadable). Bounded text: the bridge parser and Sentry sanitize further.
  function errText(e) {
    try { return String(e && e.name ? e.name + ': ' + e.message : e).slice(0, 80); } catch (x) { return 'unknown'; }
  }

  try {
    // --- Preamble: state auto-detection (before any network call) ---
    // Bridge hardening (spec §9): undefined = page still loading or signed out -> not signed in yet (the window
    // stays open). A defined value that is not a sportsbook state (US-SB) is ALSO what a logged-out session gets
    // (slipsync-track-b-dk.md, verified 2026-07-24), so it waits too; DFS-only and NV users reach the inactivity
    // timeout, as before this change. Only a matched state outside V1 is "not available here".
    var se = window.__siteExperience;
    if (typeof se !== 'string') { emit({ type: 'auth_error', reason: 'no_site_experience' }); return; }
    var m = se.match(/^US-([A-Z]{2})-SB$/);
    if (!m) { emit({ type: 'auth_error', reason: 'no_sportsbook_state' }); return; }
    var state = m[1];
    if (cfg.states.indexOf(state) === -1) {
      // Valid sportsbook state, but not yet supported in V1.
      emit({ type: 'api_error', error: 'not_available_in_state' });
      return;
    }

    var jwtUrl = 'https://gaming-us-' + state.toLowerCase() +
      '.draftkings.com/api/wager/v1/generateEnterpriseJWT';

    // --- Step 1: GET Enterprise JWT (fresh every sync, never cached) ---
    // The rejection handler sits on the fetch itself so a network/CORS/abort failure
    // (jwt_fetch_rejected) is told apart from a later throw (script_exception).
    fetch(jwtUrl, { credentials: 'include', headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        // Bridge hardening: every pre-JWT failure means not signed in yet (the window stays open through sign-in).
        if (res.status === 401 || res.status === 403) { emit({ type: 'auth_error', reason: 'http_status' }); return null; }
        if (!res.ok) { emit({ type: 'auth_error', reason: 'http_error' }); return null; }
        // HTML-200 = bot-detection/login interstitial. Check Content-Type before parsing.
        var ct = (res.headers.get('content-type') || '');
        if (ct.indexOf('application/json') === -1) { emit({ type: 'auth_error', reason: 'html_response' }); return null; }
        // Fixed code, never the parser's message: WebKit can quote the body (a token) in it.
        return res.json().then(null, function () { emit({ type: 'auth_error', reason: 'unexpected_shape' }); return null; });
      }, function () {
        // A navigation during sign-in aborts this fetch: not signed in yet, never terminal.
        emit({ type: 'auth_error', reason: 'network_error' });
        return null;
      })
      .then(function (body) {
        if (done) return;
        if (!body || !body.token) { emit({ type: 'auth_error', reason: 'unexpected_shape' }); return; }
        openSocket(body.token);
      })
      .catch(function (e) { emit({ type: 'script_error', error: 'script_exception: ' + errText(e) }); });

    // --- Steps 2-4: open socket, send BetsRequest, collect matching frame ---
    function openSocket(token) {
      var url = cfg.socketBase + '?format=json&jwt=' + encodeURIComponent(token);
      function makeId() {
        return (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : ('dk-' + Date.now() + '-' + Math.round(Math.random() * 1e9));
      }
      var requestId = makeId();
      var timer = null;
      // #1712 PR A open-bet probe state: the held settled message and the probe's own bookkeeping.
      var held = null;
      var probe = null;
      // #1714: page 2 while it is outstanding ({ id, timer, page1 }), and whether it was ever sent (§3.5).
      var page2 = null;
      var page2Sent = false;

      try {
        socket = new WebSocket(url);
      } catch (e) {
        emit({ type: 'api_error', error: 'socket_ctor' });
        return;
      }

      // #1714 U9: skip defaults to 0 so no call site can drop the key.
      function betsRequest(id, status, skip) {
        return JSON.stringify({
          jsonrpc: '2.0',
          method: 'BetsRequest',
          id: id,
          params: {
            filter: { status: status },
            orderCriteria: { orderBy: 'placementDate', direction: 'DESC' },
            pagination: { count: 25, skip: typeof skip === 'number' ? skip : 0 },
            locale: 'en-US',
            ScoreboardType: 'EventScore',
          },
        });
      }

      socket.onopen = function () {
        // Params must match the captured DK BetsRequest exactly (spec §7, Camarda
        // capture 2026-07-18) — DK schema-rejects a mismatched shape, which returns
        // as a JSON-RPC frame.error → api_error, failing first-time sync before any
        // bets reach /api/slipsync. orderCriteria is an OBJECT (not an array), and
        // ScoreboardType + locale are part of the captured request. (locale value is
        // not recorded in the capture; 'en-US' is the canonical US-NY sportsbook locale.)
        socket.send(betsRequest(requestId, 'Settled'));
        // Bounded 10s internal timeout for the matching frame (fires well before the
        // 180s bridge timeout).
        timer = setTimeout(function () { emit({ type: 'api_error', error: 'frame_timeout' }); }, 10000);
      };

      socket.onmessage = function (ev) {
        if (done) return;
        if (held) { onProbeMessage(ev); return; }
        if (page2) { onPage2Message(ev); return; }
        var frame;
        try {
          frame = JSON.parse(ev.data);
        } catch (e) {
          if (timer) { clearTimeout(timer); }
          emit({ type: 'script_error', error: 'frame_parse_failed' });
          return;
        }
        // Ignore server pushes / interleaved responses that do not match our request id. #1716: JSON-RPC
        // answers a request it cannot parse (-32700) or validate (-32600) with id:null, and this socket
        // carries only our one request, so those two reserved codes are our request's rejection. Any other
        // null-id frame stays ignored: it could be an unrelated push, and ending on it would lose our answer.
        if (!frame) return;
        var nullIdError = frame.id === null && !!frame.error
          && (frame.error.code === -32700 || frame.error.code === -32600);
        if (frame.id !== requestId && !nullIdError) return;
        if (timer) { clearTimeout(timer); }
        // #1711: each api_error carries a fixed code. A JSON-RPC error adds its integer code (int32) only,
        // never DK's message text.
        if (frame.error) {
          var code = frame.error && frame.error.code;
          var hasCode = typeof code === 'number' && Math.floor(code) === code && code >= -2147483648 && code <= 2147483647;
          emit({ type: 'api_error', error: hasCode ? 'rpc_error ' + code : 'rpc_error' });
          return;
        }
        var bets = frame.result && frame.result.bets;
        if (!Array.isArray(bets)) { emit({ type: 'api_error', error: 'bets_not_array' }); return; }
        // Bound the array at the trust boundary: we requested count:25, so a frame with
        // >=25 signals more history exists. Slice to 25 so a hostile/oversized frame cannot push an
        // unbounded payload across the bridge, then read page 2 (#1714).
        if (bets.length >= 25) {
          startPage2(bets.slice(0, 25));
        } else {
          startProbeOrEmit({ type: 'betslips', data: bets });
        }
      };

      // Bridge hardening (P2-8): a socket closed before the bets frame is usually a navigation right after
      // sign-in, so it is a retryable script_error (the bridge waits for the scheduled re-inject), not a book error.
      socket.onerror = function () {
        if (timer) { clearTimeout(timer); }
        if (held) { closeProbe(); return; }
        if (page2) { page2Closed(); return; }
        emit({ type: 'script_error', error: 'socket_closed' });
      };

      socket.onclose = function () {
        if (timer) { clearTimeout(timer); }
        if (held) { closeProbe(); return; }
        if (page2) { page2Closed(); return; }
        // If a terminal message already fired, the done guard makes this a no-op.
        emit({ type: 'script_error', error: 'socket_closed' });
      };

      // ---- #1714: page 2 with a one-bet overlap (spec docs/superpowers/specs/2026-11-01-1714-dk-paging-design.md §3) ----
      // Page 2 asks for skip 24, so its first bet should be page 1's last (the anchor): seeing it proves DK
      // honoured skip. Every way page 2 goes wrong keeps page 1 and names the reason in stoppedAt.
      var SETTLED_BYTE_BUDGET = 400000;
      var MAX_SHIFT = 3;
      function betKey(b) {
        if (!b || typeof b !== 'object') return null;
        var id = b.betId;
        return (typeof id === 'string' || typeof id === 'number') ? '#' + String(id) : null;
      }
      function page1Partial(p1, code) {
        return { type: 'partial', data: p1, stoppedAt: code };
      }
      function endPage2() {
        var p = page2;
        page2 = null;
        clearTimeout(p.timer); // clearTimeout(null) is a no-op
        return p.page1;
      }
      function startPage2(p1) {
        if (byteLen(JSON.stringify(p1)) > SETTLED_BYTE_BUDGET) { startProbeOrEmit(page1Partial(p1, 'size_cap')); return; }
        try {
          page2 = { id: null, timer: null, page1: p1 };
          // U2: the timer is armed before send, so a send that never answers still ends.
          page2.timer = setTimeout(onPage2Timeout, 10000);
          page2.id = makeId();
          page2Sent = true;
          socket.send(betsRequest(page2.id, 'Settled', 24));
        } catch (e) {
          if (page2) endPage2();
          startProbeOrEmit(page1Partial(p1, 'page2_exception'));
        }
      }
      // U6: the socket is stalled or gone, so these emit directly: no probe, and the once-flag stays unset.
      function onPage2Timeout() {
        if (!page2 || done) return;
        emit(page1Partial(endPage2(), 'page2_timeout'));
      }
      function page2Closed() {
        emit(page1Partial(endPage2(), 'page2_closed'));
      }
      function onPage2Message(ev) {
        var p1 = page2.page1;
        try {
          var frame;
          try { frame = JSON.parse(ev.data); } catch (x) { return; } // U8: not JSON (a heartbeat?), the timer bounds the wait
          if (!frame || typeof frame !== 'object') return;
          var nullIdError = frame.id === null && !!frame.error
            && (frame.error.code === -32700 || frame.error.code === -32600);
          if (frame.id !== page2.id && !nullIdError) return;
          endPage2();
          if (frame.error) {
            var m = page1Partial(p1, 'page2_rpc_error');
            if (isInt32(frame.error.code)) m.page2Code = frame.error.code;
            startProbeOrEmit(m);
            return;
          }
          var bets = frame.result && frame.result.bets;
          if (!Array.isArray(bets)) { startProbeOrEmit(page1Partial(p1, 'page2_not_array')); return; }
          startProbeOrEmit(mergePage2(p1, bets.slice(0, 25)));
        } catch (e) {
          if (page2) endPage2();
          startProbeOrEmit(page1Partial(p1, 'page2_exception'));
        }
      }
      function mergePage2(p1, raw) {
        if (raw.length === 0) return page1Partial(p1, 'page2_empty');
        var seen = {};
        for (var i = 0; i < p1.length; i++) {
          var k1 = betKey(p1[i]);
          if (k1 !== null) seen[k1] = true;
        }
        var anchor = betKey(p1[p1.length - 1]);
        var anchorAt = -1;
        var fresh = [];
        for (var j = 0; j < raw.length; j++) {
          var b = raw[j];
          if (!b || typeof b !== 'object') continue;
          var k = betKey(b);
          var repeat = k !== null && Object.prototype.hasOwnProperty.call(seen, k);
          // The anchor proves skip only near the top: a window shifted by up to MAX_SHIFT bets settling between
          // the two requests. DK ignoring skip returns page 1 again, with the anchor at index 24.
          if (anchorAt === -1 && anchor !== null && k === anchor && j <= MAX_SHIFT) anchorAt = j;
          if (!repeat) fresh.push(b);
        }
        // Pre-Codex (fail-closed P2): a working skip puts only page-1 bets ahead of the anchor.
        var verified = anchorAt !== -1;
        for (var q = 0; verified && q < anchorAt; q++) {
          var kq = betKey(raw[q]);
          if (kq === null || !Object.prototype.hasOwnProperty.call(seen, kq)) verified = false;
        }
        // History ends at bet 25 only when page 2 holds nothing past the anchor; any other all-repeat page is
        // DK not honouring skip.
        if (fresh.length === 0) {
          return verified && raw.length === anchorAt + 1 ? { type: 'betslips', data: p1 } : page1Partial(p1, 'page2_repeat');
        }
        // Size guard: page 1 is never trimmed; fresh bets are appended while the array stays within budget.
        var data = p1.slice();
        var size = byteLen(JSON.stringify(data));
        var trimmed = false;
        for (var f = 0; f < fresh.length; f++) {
          var add = byteLen(JSON.stringify(fresh[f])) + 1;
          if (size + add > SETTLED_BYTE_BUDGET) { trimmed = true; break; }
          data.push(fresh[f]);
          size += add;
        }
        if (trimmed) return { type: 'partial', data: data, stoppedAt: 'size_cap' };
        if (!verified) return { type: 'partial', data: data, stoppedAt: 'page2_unverified' };
        if (raw.length >= 25) return { type: 'partial', data: data, stoppedAt: 'cap' };
        return { type: 'betslips', data: data };
      }

      // ---- #1712 PR A: open-bet probe (spec docs/superpowers/specs/2026-11-01-1712-dk-open-bet-probe-design.md §3.1) ----
      // After a good settled answer, ask once per page for open bets with each probe word, hold the settled
      // message, and emit it with the probe's outcomes. Nothing in the probe can fail or drop the settled
      // result: every probe path is wrapped, and one 5 s deadline bounds the whole probe.
      var PROBE_WORDS = ['Open', 'Unsettled', 'Pending'];
      function startProbeOrEmit(msg) {
        if (held || done) return; // #1714 U1: one settled result per run, whichever path reaches here first
        if (window.__stDkOpenProbe) { emit(msg); return; }
        held = msg;
        probe = { ids: {}, out: {}, unattributed: [], error: null, deadline: null };
        try {
          window.__stDkOpenProbe = true;
          probe.deadline = setTimeout(function () { finishProbe(); }, 5000);
          for (var i = 0; i < PROBE_WORDS.length; i++) {
            var id = makeId();
            probe.ids[id] = PROBE_WORDS[i];
            socket.send(betsRequest(id, PROBE_WORDS[i]));
          }
        } catch (e) {
          probe.error = 'probe_exception';
          finishProbe();
        }
      }
      function outstandingWords() {
        var left = [];
        for (var i = 0; i < PROBE_WORDS.length; i++) if (!probe.out[PROBE_WORDS[i]]) left.push(PROBE_WORDS[i]);
        return left;
      }
      function isInt32(c) {
        return typeof c === 'number' && Math.floor(c) === c && c >= -2147483648 && c <= 2147483647;
      }
      function onProbeMessage(ev) {
        try {
          var frame;
          try { frame = JSON.parse(ev.data); } catch (x) { return; } // not JSON (a heartbeat?): ignored
          if (!frame || typeof frame !== 'object') return;
          var word = typeof frame.id === 'string' && Object.prototype.hasOwnProperty.call(probe.ids, frame.id) ? probe.ids[frame.id] : null;
          if (word) {
            if (probe.out[word]) return; // a late duplicate answer
            if (frame.error) {
              var code = frame.error && frame.error.code;
              probe.out[word] = isInt32(code) ? { word: word, outcome: 'rpc_error', code: code } : { word: word, outcome: 'rpc_error' };
            } else if (frame.result && Array.isArray(frame.result.bets)) {
              var bets = frame.result.bets;
              probe.out[word] = { word: word, outcome: 'frame', count: Math.min(bets.length, 1000), bets: bets.slice(0, 10) };
            } else {
              var r = frame.result;
              probe.out[word] = (r && typeof r === 'object' && !Array.isArray(r))
                ? { word: word, outcome: 'not_array', result: r } : { word: word, outcome: 'not_array' };
            }
          } else if (frame.id === null && frame.error && (frame.error.code === -32700 || frame.error.code === -32600)) {
            // #1716: a rejection DK cannot tie to a request. Attributed only when exactly one word is outstanding.
            // #1714 §3.5: once page 2 was sent it may be page 2's late rejection, so it is never pinned on a word.
            if (page2Sent) {
              if (probe.unattributed.length < 3) probe.unattributed.push(frame.error.code);
              return;
            }
            var left = outstandingWords();
            if (left.length === 1) {
              probe.out[left[0]] = { word: left[0], outcome: 'rpc_error', code: frame.error.code };
            } else if (left.length > 1) {
              if (probe.unattributed.length < 3) probe.unattributed.push(frame.error.code);
              if (probe.unattributed.length >= left.length) {
                for (var j = 0; j < left.length; j++) probe.out[left[j]] = { word: left[j], outcome: 'rpc_error_unattributed' };
              }
            }
          } else {
            return;
          }
          if (outstandingWords().length === 0) finishProbe();
        } catch (e) {
          probe.error = 'probe_exception';
          finishProbe();
        }
      }
      function closeProbe() {
        try {
          var left = outstandingWords();
          for (var i = 0; i < left.length; i++) probe.out[left[i]] = { word: left[i], outcome: 'socket_closed' };
        } catch (e) {
          probe.error = 'probe_exception';
        }
        finishProbe();
      }
      function byteLen(str) {
        try { return new TextEncoder().encode(str).length; } catch (e) { return str.length * 3; }
      }
      // Keep the combined body under the route's 512 000-byte cap: shrink each attempt's bets to 3, then 1,
      // then drop result, then drop bets (last word first). count, outcomes and codes always stay.
      function fitBudget(op) {
        var budget = Math.min(200000, 480000 - byteLen(JSON.stringify(held.data)));
        var dropped = 0;
        var a = op.attempts;
        function over() { return byteLen(JSON.stringify(op)) > budget; }
        var limits = [3, 1];
        for (var l = 0; l < limits.length && over(); l++) {
          for (var i = 0; i < a.length; i++) {
            if (a[i].bets && a[i].bets.length > limits[l]) { dropped += a[i].bets.length - limits[l]; a[i].bets = a[i].bets.slice(0, limits[l]); }
          }
        }
        for (var k = a.length - 1; k >= 0 && over(); k--) delete a[k].result;
        for (var m = a.length - 1; m >= 0 && over(); m--) {
          if (a[m].bets) { dropped += a[m].bets.length; delete a[m].bets; }
        }
        if (dropped > 0) op.betsDropped = Math.min(dropped, 1000);
        // Pre-Codex fail-closed P2: a settled body already near the cap gets no probe at all; the probe must
        // never push an import that fits over the route's limit.
        return !over();
      }
      function finishProbe() {
        if (done) return;
        try { clearTimeout(probe.deadline); } catch (e) { /* no deadline armed */ }
        var op;
        try {
          op = { attempts: [] };
          for (var i = 0; i < PROBE_WORDS.length; i++) {
            op.attempts.push(probe.out[PROBE_WORDS[i]] || { word: PROBE_WORDS[i], outcome: 'timeout' });
          }
          if (probe.unattributed.length) op.unattributed = probe.unattributed;
          if (probe.error) op.error = probe.error;
          if (!fitBudget(op)) op = null;
        } catch (e) {
          // Last resort: outcomes, counts and codes only (the shape both parsers accept), with the probe marked
          // broken, so the settled result still goes out.
          op = { attempts: [], error: 'probe_exception' };
          for (var j = 0; j < PROBE_WORDS.length; j++) {
            var o = probe.out[PROBE_WORDS[j]];
            var slim = { word: PROBE_WORDS[j], outcome: o ? o.outcome : 'timeout' };
            if (o && o.count !== undefined) slim.count = o.count;
            if (o && o.code !== undefined) slim.code = o.code;
            op.attempts.push(slim);
          }
        }
        if (op) held.openProbe = op;
        emit(held);
      }
    }
  } catch (e) {
    emit({ type: 'script_error', error: 'script_exception: ' + errText(e) });
  }
}

// Serialize the real entrypoint into a self-contained IIFE for InAppBrowser.executeScript. #1409: the bridge's
// per-open nonce joins the config; no valid nonce, no script.
function getDkFetchScript(nonce) {
  if (!isBridgeNonce(nonce)) throw new Error('Invalid bridge nonce');
  return '(' + dkInjectedMain.toString() + ')(' + JSON.stringify({ ...DK_INJECT_CFG, nonce }) + ');';
}

export const dkConfig = {
  key: 'dk',
  displayName: 'DraftKings',
  stage: 'import',
  needsRegionSelection: false, // state auto-detected via window.__siteExperience (§7)
  validRegions: null, // no region gate — see useSlipSync bypass
  getWebViewUrl: () => DK_WEBVIEW_URL, // state-agnostic; DK routes from the authed session
  getFetchScript: (_region, nonce) => getDkFetchScript(nonce),
  scriptHosts: ['draftkings.com'], // #1409: the fetch script runs only on these hosts (and their subdomains)
  webViewTitle: 'Sign in to DraftKings to sync',
  errorMessages: {
    authError:
      'DraftKings login required. Open DraftKings Sportsbook and sign in, then try again. '
      + '(Sync is unavailable on DFS-only accounts or in states without a DK sportsbook.)',
    apiError: 'Unable to fetch DraftKings bets. Please try again.',
    openFailed: 'Unable to open DraftKings. Please try again.',
    noBetsFound: 'No settled bets found on your DraftKings account.',
    notAvailableInState: 'DraftKings sync is not yet available in your state.',
  },
};
